import React, { useState } from "react";
import { message } from "antd";
import { SendOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { ButtonItem, ModalItem, InputItem, TextAreaItem, SelectItem, HeaderItem } from "../../../../Components/Items";
import useEsMovil from "./useEsMovil";
import {
  OPCIONES_TIPO_INCIDENCIA,
  MIN_DESCRIPCION,
  MIN_JUSTIFICACION,
  MIN_MOTIVO_NO_ENVIO,
  textoSuficiente,
} from "./fasesSoporte";

const AccionesPeticion = ({ ticket, headersToken, onRevisada, compacto }) => {
  const esMovil = useEsMovil();
  const [modal, setModal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [tipoIncidencia, setTipoIncidencia] = useState(undefined);
  const [descripcion, setDescripcion] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [motivo, setMotivo] = useState("");

  if (!ticket || ticket.fase !== "en_revision") return null;

  const abrirAutorizar = () => {
    setTitulo(ticket.titulo || "");
    setTipoIncidencia(ticket.tipoIncidencia);
    setDescripcion(ticket.descripcion || "");
    setJustificacion("");
    setModal("autorizar");
  };

  const abrirNoEnviar = () => {
    setMotivo("");
    setModal("noEnviar");
  };

  const enviar = (ruta, cuerpo, textoExito) => {
    setGuardando(true);
    axios
      .post(ruta, { ticketId: ticket._id, ...cuerpo }, { headers: headersToken })
      .then((r) => {
        if (r.data.success === 2) {
          message.success(textoExito);
          setModal(null);
          onRevisada?.();
        } else {
          message.info(r.data.message || "Vuelve a intentarlo en un momento, por favor.");
        }
      })
      .catch(() => message.info("Revisa tu conexión e intenta de nuevo."))
      .finally(() => setGuardando(false));
  };

  const autorizar = () => {
    if (!titulo.trim()) return message.info("Ponle un título a la petición antes de continuar.");
    if (!tipoIncidencia) return message.info("Elige el tipo de incidencia antes de continuar.");
    if (!textoSuficiente(descripcion, MIN_DESCRIPCION)) {
      return message.info("La descripción es muy corta, cuéntanos un poco más.");
    }
    if (!textoSuficiente(justificacion, MIN_JUSTIFICACION)) {
      return message.info("Tu justificación es muy corta, cuéntanos un poco más.");
    }
    enviar(
      "soporte/autorizarPeticion",
      { titulo: titulo.trim(), tipoIncidencia, descripcion: descripcion.trim(), justificacion: justificacion.trim() },
      "Listo, la petición ya está con el equipo de soporte.",
    );
  };

  const noEnviar = () => {
    if (!textoSuficiente(motivo, MIN_MOTIVO_NO_ENVIO)) {
      return message.info("Tu comentario es muy corto, cuéntanos un poco más.");
    }
    enviar("soporte/noEnviarPeticion", { motivo: motivo.trim() }, "Listo, la petición quedó atendida en tu empresa.");
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexDirection: compacto && esMovil ? "column" : "row" }}>
        <ButtonItem verdeNatural icon={<SendOutlined />} onClick={abrirAutorizar} block={compacto && esMovil}>
          Autorizar y enviar a soporte
        </ButtonItem>
        <ButtonItem cyanB icon={<CheckCircleOutlined />} onClick={abrirNoEnviar} block={compacto && esMovil}>
          Atender en la empresa
        </ButtonItem>
      </div>

      <ModalItem
        visible={modal === "autorizar"}
        title={`Autorizar petición ${ticket.folio}`}
        onCancel={() => setModal(null)}
        footer={null}
        destroyOnClose
        spinning={false}
        width={esMovil ? undefined : 520}
        zIndex={1100}
      >
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Puedes corregir el título, el tipo de incidencia y la descripción antes de enviarla a soporte, o dejarlos
          como están.
        </div>
        <HeaderItem>TÍTULO</HeaderItem>
        <InputItem value={titulo} onChange={(e) => setTitulo(e.target.value)} style={{ marginBottom: 12 }} />
        <HeaderItem>TIPO DE INCIDENCIA</HeaderItem>
        <SelectItem
          placeholder="Tipo de incidencia"
          value={tipoIncidencia}
          options={OPCIONES_TIPO_INCIDENCIA}
          onChange={(value) => setTipoIncidencia(value)}
          dropdownStyle={{ zIndex: 1200 }}
          noBuscar
          style={{ width: "100%", marginBottom: 12 }}
        />
        <HeaderItem>DESCRIPCIÓN</HeaderItem>
        <TextAreaItem rows={4} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} style={{ marginBottom: 12 }} />
        <HeaderItem>JUSTIFICACIÓN PARA SOPORTE</HeaderItem>
        <TextAreaItem
          rows={4}
          placeholder="Cuéntale a soporte por qué esta petición necesita su atención"
          value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonItem verdeNatural loading={guardando} icon={<SendOutlined />} onClick={autorizar}>
            Autorizar y enviar
          </ButtonItem>
        </div>
      </ModalItem>

      <ModalItem
        visible={modal === "noEnviar"}
        title={`Atender en la empresa ${ticket.folio}`}
        onCancel={() => setModal(null)}
        footer={null}
        destroyOnClose
        spinning={false}
        width={esMovil ? undefined : 480}
        zIndex={1100}
      >
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Quien levantó la petición verá tu comentario y el chat quedará cerrado.
        </div>
        <TextAreaItem
          rows={4}
          placeholder="Ej. Ya está reportado en el folio TIC-00012, te avisamos en cuanto quede."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ButtonItem cyan loading={guardando} icon={<CheckCircleOutlined />} onClick={noEnviar}>
            Guardar
          </ButtonItem>
        </div>
      </ModalItem>
    </>
  );
};

export default AccionesPeticion;
